import React, { useMemo } from 'react';
import { useLocation, NavLink } from 'react-router';
import { useAuth } from '../contexts/AuthContext';
import { navigationConfig } from '../config/navigation';
import { cn } from '@/lib/utils';

interface NavigationTabsProps {
  className?: string;
}

export const NavigationTabs: React.FC<NavigationTabsProps> = ({ className }) => {
  const { user, hasPermission } = useAuth();
  const location = useLocation();

  // Find active parent item and its sub-items
  const activeModule = useMemo(() => {
    // Sort by path length desc to match most specific path first
    const sortedConfig = [...navigationConfig].sort((a, b) => b.path.length - a.path.length);
    
    return sortedConfig.find(item => 
      location.pathname.startsWith(item.path)
    );
  }, [location.pathname]);

  const visibleSubItems = useMemo(() => {
    if (!activeModule?.subItems) return [];
    
    return activeModule.subItems.filter(subItem => {
      if (subItem.adminOnly && user?.role !== 'Admin') return false;
      if (subItem.permission && !hasPermission(subItem.permission)) return false;
      return true;
    });
  }, [activeModule, user, hasPermission]);

  if (visibleSubItems.length === 0 && !activeModule) return null;

  return (
    <div className={cn("flex items-center gap-1 overflow-x-auto no-scrollbar py-1", className)}>
      {visibleSubItems.length > 0 ? (
        visibleSubItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            end={item.end}
            className={({ isActive }) => `
              px-3 py-1.5 rounded-md text-sm font-medium transition-colors whitespace-nowrap flex items-center gap-2 flex-shrink-0
              ${isActive 
                ? 'bg-slate-100 text-slate-900 border border-slate-200 shadow-sm' 
                : 'text-slate-500 hover:text-slate-900 hover:bg-slate-50'
              }
            `}
          >
            {item.icon && <item.icon size={14} className="opacity-70" />}
            {item.label}
          </NavLink>
        ))
      ) : (
        /* Fallback title if no sub-items but active module found */
        <h2 className="text-sm font-semibold text-slate-900 flex items-center gap-2 px-1">
          {activeModule?.icon && <activeModule.icon size={16} className="text-slate-500" />}
          {activeModule?.label}
        </h2>
      )}
    </div>
  );
};
